import styled from '@emotion/styled';
import theme from '../../themes/default';
import Button from '../Button/Button';

const { spacing, font, color } = theme;
const {
  purple70,
  background,
  blackOpacity12,
  blackOpacity38,
} = theme.color.designSystem;
const fontFamily = font.family.workSans;

const defaultFontFamily = {
  fontFamily,
  fontSize: 14,
};

const svgStyle = {
  right: spacing.xSmall,
  transition: '150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
};

const rootStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  '&$focused $notchedOutline': {
    border: `1px solid ${color.grey13}`,
  },
  '&$focused $mui-selectMenu': {
    background: background.grey.focus,
  },
  '& svg': {
    ...svgStyle,
  },
};

const rootStyleWithError = {
  display: 'flex',
  flexWrap: 'wrap',
  '& $notchedOutline': {
    borderColor: color.designSystem.error,
    borderWidth: '2px',
  },
  '&$focused $notchedOutline': {
    borderColor: color.designSystem.error,
  },
  '&:hover $notchedOutline': {
    borderColor: color.designSystem.error,
  },
  '&$focused svg': {
    ...svgStyle,
    color: color.designSystem.error,
  },
};

const rootInputStyle = {
  ...defaultFontFamily,
  fontSize: '14px', // MUI scales the label size to .75, so 16px = 12px
  transform: 'translate(16px, -6px) scale(0.75) !important',
};

const getInputStyles = () => ({
  root: {
    ...rootStyle,
  },
  rootWithValue: {
    ...rootStyle,
  },
  rootOpen: {
    ...rootStyle,
    '&$focused $notchedOutline': {
      borderColor: purple70,
    },
    '& svg': {
      ...svgStyle,
      color: purple70,
      transform: 'rotate(180deg)',
    },
  },
  rootDisabled: {
    ...rootStyle,
    '& $notchedOutline': {
      border: `1px solid ${blackOpacity12} !important`,
    },
    '& svg': {
      ...svgStyle,
      color: blackOpacity38,
    },
  },
  error: {
    ...rootStyleWithError,
    '& svg': {
      ...svgStyle,
    },
    '& $notchedOutline': {
      borderColor: color.designSystem.error,
      borderWidth: '2px',
    },
  },
  openWithError: {
    ...rootStyleWithError,
    '& svg': {
      ...svgStyle,
      color: color.designSystem.error,
      transform: 'rotate(180deg)',
    },
  },

  errorWithValue: {
    ...rootStyleWithError,
    '& svg': {
      ...svgStyle,
    },
  },

  input: {
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  formControl: {
    ...defaultFontFamily,
    marginTop: spacing.xSmall,
    width: '100%',
  },
  select: {
    ...defaultFontFamily,
  },
  selectInputSelected: {
    ...defaultFontFamily,
    paddingTop: spacing.xSmall,
    paddingBottom: spacing.xSmall,
    '&:focus': {
      background: background.grey.focus,
    },
  },
  selectInput: {
    ...defaultFontFamily,
    margin: 0,
    height: 'auto',
    fontWeight: font.weight.regular,
    paddingTop: spacing.xSmall,
    paddingBottom: spacing.xSmall,
    '&:hover': {
      background: background.grey.hover,
    },
    '&:focus': {
      background: background.grey.hover,
    },
    '&$selected': {
      background: background.grey.selected,
    },
  },

  selectInputCustom: {
    ...defaultFontFamily,
    margin: 0,
    height: 'auto',
    fontWeight: font.weight.regular,
    padding: '0px',
    '&:hover': {
      background: background.grey.hover,
    },
    '&:focus': {
      background: background.grey.hover,
    },
    '&$selected': {
      background: background.grey.selected,
    },
  },

  selectInputCustomFooter: {
    ...defaultFontFamily,
    margin: 0,
    height: 'auto',
    fontWeight: font.weight.regular,
    padding: '0px',
    pointerEvents: 'none',
    '&:hover': {
      background: background.grey.hover,
    },
    '&:focus': {
      background: background.grey.hover,
    },
    '&$selected': {
      background: background.grey.selected,
    },
  },

  selectInputDisabled: {
    ...defaultFontFamily,
    margin: 0,
    height: 'auto',
    fontWeight: font.regular,
    padding: '0px',
    pointerEvents: 'none',
  },

  selectInputLabel: {
    ...rootInputStyle,
    marginLeft: '-2px',
  },

  selectInputLabelDisabled: {
    ...rootInputStyle,
    color: `${blackOpacity38} !important`,
  },
  selectInputLabelInLine: {
    top: '0px',
    marginTop: '18px',
    fontSize: '19px',
    lineHeight: font.lineHeight.copy,
    transform: 'translate(16px, -6px) scale(0.75) !important',
    marginLeft: '-2px',
  },

  selectInputLabelInLineWithError: {
    top: '0px',
    marginTop: '18px',
    fontSize: '19px',
    lineHeight: font.lineHeight.copy,
    transform: 'translate(16px, -6px) scale(0.75) !important',
    marginLeft: '-2px',
    color: `${color.designSystem.error} !important`,
  },

  selectInputLabelWithError: {
    ...rootInputStyle,
    color: `${color.designSystem.error} !important`,
    marginLeft: '-2px',
  },
  errorText: {
    color: color.designSystem.error,
  },
  selectInputLabelFocused: {
    /*
     * MUI CSS selector scoring is bigger than what we can have by passing a custom class
     * That's why we have to use !important.
     * TO DO - remove it once MUI applies the custom focused classes with bigger scoring
     */
    fontSize: font.large,
    color: `${purple70} !important`,
  },
  selectInputLabelFocusedInline: {
    /*
     * MUI CSS selector scoring is bigger than what we can have by passing a custom class
     * That's why we have to use !important.
     * TO DO - remove it once MUI applies the custom focused classes with bigger scoring
     */
    color: `${color.text} !important`,
  },
  selectInputLabelFocusedWithError: {
    /*
     * MUI CSS selector scoring is bigger than what we can have by passing a custom class
     * That's why we have to use !important.
     * TO DO - remove it once MUI applies the custom focused classes with bigger scoring
     */
    color: `${color.designSystem.error} !important`,
  },

  selectList: {
    padding: 0,
  },
  'mui-selectMenu': {},
  notchedOutline: {},
  focused: {},
  selected: {},
});

export const HiddenInput = styled('div')`
  position: relative;
  visibility: hidden;
  width: max-content;
  padding-left: 14px;
  font-size: ${font.small};
  height: 0;
`;

export const ButtonsStyled = styled('div')`
  background-color: ${color.white};
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid ${color.designSystem.blackOpacity12};
  padding: ${spacing.xSmall};
  pointer-events: none;
`;

export const ButtonStyled = styled(Button)`
  pointer-events: auto;
`;

export const PrimaryButtonStyled = styled(Button)`
  pointer-events: auto;
  margin-left: ${spacing.xSmall}; ;
`;

export const RadioContainer = styled('div')`
  width: 100%;
`;

export default getInputStyles;
